import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2, MessageCircle, Trash2, Barcode, Share2, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { trackerApi } from '@/lib/api';
import GlassTracker from '@/components/GlassTracker';
import CoachChat from '@/components/CoachChat';
import ShareMenu from '@/components/ShareMenu';
import BarcodeScannerModal from '@/components/BarcodeScannerModal';
import ServingSizeDialog from '@/components/ServingSizeDialog';
import { MicButton } from '@/components/MicButton';

const readAsDataURL = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const Tracker = () => {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('food');
  const [summary, setSummary] = useState({ today_logs: [], week_logs: [], calorie_today: 0, vitamins_week: 0, gym_week: 0, goals: { calorie_goal: 2000, vitamin_weekly_goal: 21, gym_weekly_goal: 3 } });
  const [goals, setGoals] = useState({ calorie_goal: 2000, vitamin_weekly_goal: 21, gym_weekly_goal: 3 });
  const [analyzing, setAnalyzing] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachContext, setCoachContext] = useState({});
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [frequentFoods, setFrequentFoods] = useState([]);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [logging, setLogging] = useState(false);
  const [mealText, setMealText] = useState('');
  const [mealLoading, setMealLoading] = useState(false);

  const logMealText = async () => {
    const text = mealText.trim();
    if (!text) return;
    setMealLoading(true);
    try {
      const res = await trackerApi.analyzeText(text);
      await trackerApi.log({ kind: 'food', name: res.name || text, value: res.calories || 0, meta: { ...res, source: 'voice' } });
      toast.success(`${res.name || 'Meal'} · ${res.calories || 0} cal`, { description: res.verdict || '' });
      setMealText('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not log that meal');
    } finally { setMealLoading(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    refresh();
  }, [user, authLoading, nav]);

  const refresh = async () => {
    try {
      const data = await trackerApi.summary();
      setSummary(data);
      setGoals(data.goals);
    } catch (e) { /* ignore */ }
    try {
      const { items } = await trackerApi.frequentFoods();
      setFrequentFoods(items || []);
    } catch (e) { /* ignore */ }
  };

  const foodLogs = summary.today_logs.filter((l) => l.kind === 'food');
  const vitLogs = summary.week_logs.filter((l) => l.kind === 'vitamin');
  const gymLogs = summary.week_logs.filter((l) => l.kind === 'gym');

  const calorieTotal = summary.calorie_today || 0;
  const vitTotal = summary.vitamins_week || 0;
  const gymTotal = summary.gym_week || 0;

  const lookupBarcode = async (rawCode) => {
    const code = (rawCode ?? barcodeInput).toString().trim();
    if (!code) return;
    setBarcodeLoading(true);
    try {
      const p = await trackerApi.barcode(code);
      setBarcodeInput('');
      setPendingProduct(p);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Barcode not found');
    } finally { setBarcodeLoading(false); }
  };

  const handleScanned = (code) => {
    setScannerOpen(false);
    lookupBarcode(code);
  };

  const confirmServing = async (entry) => {
    setLogging(true);
    try {
      await trackerApi.log({ kind: 'food', name: entry.name, value: entry.calories, meta: entry.meta || {} });
      toast.success(`${entry.name} · ${entry.calories} cal logged`);
      setPendingProduct(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not log');
    } finally { setLogging(false); }
  };

  const quickLog = async (item) => {
    try {
      await trackerApi.log({ kind: 'food', name: item.name, value: item.value || 0, meta: item.meta || {} });
      toast.success(`${item.name} · ${Math.round(item.value || 0)} cal logged`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not log');
    }
  };

  const uploadAndAnalyze = async (file, kind) => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const b64 = await readAsDataURL(file);
      const res = await trackerApi.analyze({ image_b64: b64, kind });
      if (kind === 'food') {
        await trackerApi.log({ kind, name: res.name || 'Meal', value: res.calories || 0, meta: res });
        toast.success(`${res.name || 'Meal'} · ${res.calories || 0} cal`, {
          description: res.verdict || '',
          action: { label: 'Ask coach', onClick: () => { setCoachContext({ meal: res.name, calories: res.calories, alternatives: res.alternatives }); setCoachOpen(true); } },
        });
      } else if (kind === 'vitamin') {
        const count = res.count || 1;
        await trackerApi.log({ kind, name: res.name || 'Supplement', value: count, meta: res });
        toast.success(`Logged ${count} × ${res.name || 'Supplement'}`, { description: res.notes || '' });
      } else if (kind === 'gym') {
        if (!res.is_workout) {
          toast.error('That doesn\u2019t look like a workout photo, try another shot');
          setAnalyzing(false); return;
        }
        await trackerApi.log({ kind, name: res.activity || 'Workout', value: 1, meta: res });
        toast.success('Workout logged 💪', { description: res.notes || '' });
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not analyze the photo');
    } finally { setAnalyzing(false); }
  };

  const deleteLog = async (id) => {
    try { await trackerApi.deleteLog(id); refresh(); } catch { toast.error('Could not delete'); }
  };

  const saveGoals = async (partial) => {
    try { await trackerApi.setGoals(partial); toast.success('Goals updated'); refresh(); } catch { toast.error('Could not save'); }
  };

  const config = {
    food: { emoji: '🍎', label: 'calories today', goal: goals.calorie_goal, progress: calorieTotal, list: foodLogs, sliderMax: 5000, sliderStep: 50, goalKey: 'calorie_goal', title: 'Food Tracker', hint: 'Snap your meal or scan a barcode. AI counts calories & suggests healthier swaps. Resets each day.', accent: '#4A7C59' },
    vitamin: { emoji: '💊', label: 'capsules this week', goal: goals.vitamin_weekly_goal, progress: vitTotal, list: vitLogs, sliderMax: 60, sliderStep: 1, goalKey: 'vitamin_weekly_goal', title: 'Vitamins Tracker', hint: 'Photograph each vitamin to log it. Set your weekly target — resets every Monday.', accent: '#7E9979' },
    gym: { emoji: '🏋️', label: 'workouts this week', goal: goals.gym_weekly_goal, progress: gymTotal, list: gymLogs, sliderMax: 14, sliderStep: 1, goalKey: 'gym_weekly_goal', title: 'Gym Tracker', hint: 'A workout selfie fills your glass. Set your weekly target — resets every Monday.', accent: '#C05746' },
  };

  const activeCfg = config[tab];
  const hitGoal = activeCfg.progress >= activeCfg.goal && activeCfg.goal > 0;
  const shareText = hitGoal
    ? `I just hit my ${tab === 'food' ? 'daily calorie' : 'weekly ' + tab} goal on Wellth 🌱 Health is the greatest wealth.`
    : `I'm ${Math.round((activeCfg.progress / activeCfg.goal) * 100)}% to my ${tab} goal on Wellth today.`;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16" data-testid="tracker-container">
      <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Today</span>
      <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2421] font-medium leading-tight">Tracker</h1>
      <p className="mt-3 text-[#4A5A53] font-light max-w-xl">Take a photo. Watch your glass fill. Ask your coach anything.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-white border border-[#E5E1D8] rounded-full p-1 h-auto">
          <TabsTrigger value="food" className="rounded-full px-5 py-2 data-[state=active]:bg-[#4A7C59] data-[state=active]:text-white" data-testid="tracker-tab-food">Food</TabsTrigger>
          <TabsTrigger value="vitamin" className="rounded-full px-5 py-2 data-[state=active]:bg-[#7E9979] data-[state=active]:text-white" data-testid="tracker-tab-vitamin">Vitamins</TabsTrigger>
          <TabsTrigger value="gym" className="rounded-full px-5 py-2 data-[state=active]:bg-[#C05746] data-[state=active]:text-white" data-testid="tracker-tab-gym">Gym</TabsTrigger>
        </TabsList>

        {Object.entries(config).map(([k, c]) => (
          <TabsContent key={k} value={k} className="mt-8">
            <div className="grid md:grid-cols-2 gap-10">
              {/* Glass */}
              <div className="flex flex-col items-center">
                <GlassTracker progress={c.progress} goal={c.goal} emoji={c.emoji} label={c.label} accent={c.accent} />
              </div>

              {/* Controls */}
              <div className="space-y-6">
                <div>
                  <h2 className="font-serif-display text-3xl text-[#1A2421] font-medium">{c.title}</h2>
                  <p className="text-[#4A5A53] mt-2 font-light">{c.hint}</p>
                </div>

                {c.sliderMax && (
                  <div>
                    <div className="flex justify-between items-baseline mb-3">
                      <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Daily goal</label>
                      <span className="text-3xl font-serif-display font-medium text-[#1A2421] tabular-nums">{c.goal}</span>
                    </div>
                    <Slider
                      value={[c.goal]}
                      min={c.sliderStep}
                      max={c.sliderMax}
                      step={c.sliderStep}
                      onValueCommit={(v) => saveGoals({ [c.goalKey]: v[0] })}
                      onValueChange={(v) => setGoals((g) => ({ ...g, [c.goalKey]: v[0] }))}
                      data-testid={`tracker-slider-${k}`}
                    />
                  </div>
                )}

                <div>
                  <label className="w-full">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => uploadAndAnalyze(e.target.files?.[0], k)}
                      disabled={analyzing}
                      data-testid={`tracker-file-${k}`}
                    />
                    <div className={`w-full rounded-full text-white flex items-center justify-center gap-2 h-14 cursor-pointer transition-colors ${analyzing ? 'opacity-60 pointer-events-none' : ''}`} style={{ background: c.accent }}>
                      {analyzing ? <><Loader2 className="w-5 h-5 animate-spin" /> Reading photo…</> : <><Camera className="w-5 h-5" strokeWidth={1.5} /> {k === 'gym' ? 'Take workout selfie' : k === 'vitamin' ? 'Photograph vitamin' : 'Photograph meal'}</>}
                    </div>
                  </label>
                </div>

                {k === 'food' && (
                  <>
                    {frequentFoods.length > 0 && (
                      <div className="space-y-2" data-testid="tracker-frequent-foods">
                        <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Recently scanned · one-tap re-log</span>
                        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                          {frequentFoods.map((it, i) => (
                            <button
                              key={i}
                              onClick={() => quickLog(it)}
                              className="shrink-0 max-w-[220px] bg-white border border-[#E5E1D8] hover:border-[#7E9979] rounded-2xl px-3 py-2 text-left transition-colors"
                              data-testid={`frequent-food-${i}`}
                              title={`${it.name} · ${Math.round(it.value || 0)} cal`}
                            >
                              <div className="text-sm font-medium text-[#1A2421] truncate max-w-[190px]">{it.name}</div>
                              <div className="text-[11px] text-[#4A5A53] mt-0.5">{Math.round(it.value || 0)} cal · logged {it.count}×</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2" data-testid="tracker-voice-meal">
                      <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Or say what you ate</span>
                      <div className="flex gap-2">
                        <MicButton testId="tracker-meal-mic" className="h-12 w-12" onText={(t) => setMealText((v) => (v ? `${v} ${t}` : t))} />
                        <Input
                          value={mealText}
                          onChange={(e) => setMealText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') logMealText(); }}
                          placeholder="e.g. two eggs and a slice of toast"
                          className="h-12 bg-white border-[#E5E1D8] rounded-full"
                          data-testid="tracker-meal-text-input"
                        />
                        <Button onClick={logMealText} disabled={mealLoading || !mealText.trim()} className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white h-12 px-5" data-testid="tracker-meal-text-log">
                          {mealLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log'}
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Input
                        value={barcodeInput}
                        onChange={(e) => setBarcodeInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') lookupBarcode(); }}
                        placeholder="Enter barcode (e.g. 737628064502)"
                        className="h-12 bg-white border-[#E5E1D8] rounded-full"
                        data-testid="tracker-barcode-input"
                      />
                      <Button onClick={() => lookupBarcode()} disabled={barcodeLoading || !barcodeInput.trim()} variant="outline" className="rounded-full border-[#E5E1D8] bg-white gap-2 h-12" data-testid="tracker-barcode-button">
                        {barcodeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Barcode className="w-4 h-4" strokeWidth={1.5} />}
                      </Button>
                    </div>
                    <Button
                      onClick={() => setScannerOpen(true)}
                      variant="outline"
                      className="w-full rounded-full border-[#E5E1D8] bg-white gap-2 h-12"
                      data-testid="tracker-scan-barcode-button"
                    >
                      <ScanLine className="w-4 h-4" strokeWidth={1.5} /> Scan a barcode with camera
                    </Button>
                    <Button
                      onClick={() => { setCoachContext({}); setCoachOpen(true); }}
                      variant="outline"
                      className="w-full rounded-full border-[#E5E1D8] bg-white gap-2 h-12"
                      data-testid="tracker-open-coach"
                    >
                      <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Ask coach about a meal
                    </Button>
                  </>
                )}

                {hitGoal && k === tab && (
                  <div className="rounded-2xl bg-[#F2EFE9] border border-[#7E9979]/40 p-4 flex items-center justify-between gap-3" data-testid="tracker-goal-hit">
                    <div>
                      <p className="font-serif-display text-xl text-[#1A2421] font-medium">Goal hit 🎉</p>
                      <p className="text-sm text-[#4A5A53] font-light">Share the win — motivate a friend.</p>
                    </div>
                    <ShareMenu label="Share" text={shareText} testId="tracker-share-button" />
                  </div>
                )}

                {/* Today's log list */}
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Today&rsquo;s log</label>
                  <ul className="mt-3 space-y-1">
                    {c.list.length === 0 && <li className="text-sm text-[#4A5A53] font-light italic">Nothing yet — snap your first {k}.</li>}
                    {c.list.map((l) => (
                      <li key={l.id} className="flex items-center justify-between bg-white border border-[#E5E1D8] rounded-xl px-3 py-2 text-sm" data-testid="tracker-log-item">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{c.emoji}</span>
                          <span className="font-medium text-[#1A2421]">{l.name}</span>
                          {k !== 'gym' && <span className="text-xs text-[#4A5A53]">· {Math.round(l.value)} {k === 'food' ? 'cal' : 'ct'}</span>}
                        </div>
                        <button onClick={() => deleteLog(l.id)} className="text-[#4A5A53] hover:text-[#C05746] p-1" aria-label="Delete">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <CoachChat open={coachOpen} onOpenChange={setCoachOpen} context={coachContext} />
      <BarcodeScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onDetected={handleScanned} />
      <ServingSizeDialog
        open={!!pendingProduct}
        product={pendingProduct}
        onCancel={() => setPendingProduct(null)}
        onConfirm={confirmServing}
        submitting={logging}
      />
    </div>
  );
};

export default Tracker;
